import { AVATAR_KINDS, DEFAULT_AVATAR_REGISTRY } from './avatarRegistry.js';

const clean = (value) => String(value || '').trim().toLowerCase();

const toIdSet = (items) => new Set(
  (items || [])
    .map((item) => (typeof item === 'string' ? item : item?.avatarId || item?.id))
    .filter(Boolean)
    .map(clean),
);

export function avatarMatchesModule(avatar, moduleId, pathname) {
  const rules = avatar?.moduleRules;
  if (!rules) return false;

  const currentModule = clean(moduleId);
  const currentPath = clean(pathname).split(/[?#]/, 1)[0];
  const moduleMatch = (rules.moduleIds || []).map(clean).includes(currentModule);
  const prefixMatch = (rules.pathPrefixes || [])
    .map(clean)
    .some((prefix) => prefix && (currentPath === prefix || currentPath.startsWith(`${prefix}/`)));
  const includeMatch = (rules.pathIncludes || [])
    .map(clean)
    .some((part) => part && currentPath.includes(part));

  return moduleMatch || prefixMatch || includeMatch;
}

function assignmentIsActiveForContext(assignment, moduleId, pathname) {
  if (typeof assignment === 'string') return true;
  if (!assignment || assignment.active === false) return false;

  const assignmentModule = clean(assignment.moduleId);
  const assignmentPrefix = clean(assignment.pathPrefix);
  if (assignmentModule && assignmentModule !== clean(moduleId)) return false;
  if (assignmentPrefix && !clean(pathname).startsWith(assignmentPrefix)) return false;
  return true;
}

/**
 * Pure resolver used by the overlay and by tests.
 *
 * Rules:
 * - Linc/system-persistent avatars are present throughout the application.
 * - Department heads appear in matching modules or when explicitly summoned.
 * - Specialists and partners appear only when assigned to this context or summoned.
 * - hiddenIds represents a user display preference, not an authorization rule.
 */
export function resolveAvatarsForModule({
  moduleId = '',
  pathname = '',
  registry = DEFAULT_AVATAR_REGISTRY,
  assignments = [],
  summons = [],
  hiddenIds = [],
} = {}) {
  const summoned = toIdSet(summons);
  const hidden = toIdSet(hiddenIds);
  const activeAssignments = new Set(
    assignments
      .filter((assignment) => assignmentIsActiveForContext(assignment, moduleId, pathname))
      .map((assignment) => clean(typeof assignment === 'string' ? assignment : assignment.avatarId || assignment.id))
      .filter(Boolean),
  );

  return Object.values(registry || {})
    .filter((avatar) => avatar && avatar.enabled !== false && !hidden.has(clean(avatar.id)))
    .filter((avatar) => {
      const id = clean(avatar.id);
      if (avatar.persistent) return true;
      if (summoned.has(id)) return true;
      if (avatar.kind === AVATAR_KINDS.SPECIALIST || avatar.kind === AVATAR_KINDS.PARTNER) {
        return activeAssignments.has(id);
      }
      if (activeAssignments.has(id)) return true;
      return avatarMatchesModule(avatar, moduleId, pathname);
    })
    .sort((a, b) => (b.priority || 0) - (a.priority || 0) || a.name.localeCompare(b.name));
}

export default resolveAvatarsForModule;
